import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import _ from "lodash";
import Link from "@fuse/core/Link";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useLoginMutation } from "@/store/services/auth";
import { toast } from "react-toastify";
import { setAccessToken, setUser, setPlan } from "@/utils/localStorage";
import {
  Paper,
  Typography,
  CircularProgress,
  Button,
  FormControlLabel,
  FormControl,
  TextField,
  Checkbox,
  IconButton,
  InputAdornment,
} from "@mui/material";
import React from "react";

/**
 * Form Validation Schema
 */
const schema = z.object({
  email: z
    .string()
    .email("You must enter a valid email")
    .nonempty("You must enter an email"),
  password: z
    .string()
    .min(4, "Password is too short - must be at least 4 chars.")
    .nonempty("Please enter your password."),
  remember_me: z.boolean().optional(),
});

type FormType = {
  email: string;
  password: string;
  remember_me?: boolean;
};

const defaultValues = {
  email: "",
  password: "",
  remember_me: false,
};

function AuthJsCredentialsSignInForm() {
  const [login, { isLoading }] = useLoginMutation();
  const { control, formState, handleSubmit } = useForm<FormType>({
    mode: "onChange",
    defaultValues,
    resolver: zodResolver(schema),
  });

  const { isValid, dirtyFields, errors } = formState;

  // State for password visibility
  const [showPassword, setShowPassword] = React.useState(false);

  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.preventDefault();
  };

  async function onSubmit(formData: FormType) {
    const { email, password, remember_me } = formData;
    const result: any = await login({ email, password, remember_me });

    if (!result.error) {
      const userData = {
        name: result.data?.data?.user?.name,
        email: result.data?.data?.user?.email,
        role: result.data?.data?.role,
        role_id: result.data?.data?.user?.role_id,
        user_id: result.data?.data?.user?.id,
        total_number_of_addresses: result.data?.data?.total_number_of_addresses,
        enabled_number_of_addresses: result.data?.data?.enabled_number_of_addresses,
      };

      const planData = {
        name: result.data?.data?.plan?.plan_name,
        id: result.data?.data?.plan?.plan_id,
        number_of_applications: result.data?.data?.plan?.number_of_applications,
        applications_created: result.data?.data?.plan?.applications_created,
        subscription_status: result.data?.data?.plan?.subscription_status,
        is_renewable: result.data?.data?.plan?.is_renewable,
        start_date: result.data?.data?.plan?.start_date,
        end_date: result.data?.data?.plan?.end_date,
        renewal_date: result.data?.data?.plan?.renewal_date,
        interval: result.data?.data?.plan?.interval,
      };

      // Store in localStorage instead of cookies
      setAccessToken(result.data?.data?.access_token);
      setUser(userData);
      setPlan(planData);

      const apiPlan = result.data?.data?.plan;
      const role = result.data?.data?.role;

      let redirectToPlans = false;

      if (!apiPlan) {
        redirectToPlans = true;
      } else if (Array.isArray(apiPlan)) {
        redirectToPlans = apiPlan.length === 0;
      } else if (typeof apiPlan === "object") {
        redirectToPlans = apiPlan.plan_id == null;
      }

      // Full page navigation so the app remounts and RTK Query / in-memory state
      // does not keep showing data from a previous session.
      const nextPath =
        redirectToPlans && role === "Applicant"
          ? "/apps/billing/plans"
          : "/dashboard";
      window.location.assign(nextPath);
    } else {
      toast.error(
        result?.error?.data?.errors[0]?.detail || "Something went wrong!",
      );
    }
  }

  return (
    <div className="flex min-w-0 h-screen flex-col items-center justify-center">
      <Paper className="bg-white w-full rounded-none px-4 py-8 sm:min-h-auto sm:w-auto sm:rounded-xl sm:p-12 sm:shadow-sm">
        <div className="mx-auto w-full max-w-80 sm:mx-0 sm:w-80 flex flex-col justify-center items-center">
          <img
            // className="w-20"
            src="/assets/images/logo/logo_loanapp.png"
            alt="ASSETS ID"
            width={165}
            height={70}
          />

          <div className="w-full mt-2">
            <Typography className="mt-8 text-4xl font-extrabold leading-[1.25] tracking-tight">
              Sign in
            </Typography>
            <div className="mt-0.5 flex items-baseline font-medium">
              <Typography>Don't have an account?</Typography>
              <Link className="ml-1" to="/sign-up">
                Sign up
              </Link>
            </div>

            <form
              name="loginForm"
              noValidate
              className="mt-8 flex w-full flex-col justify-center"
              onSubmit={handleSubmit(onSubmit)}
            >
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    className="mb-6"
                    placeholder="Email"
                    autoFocus
                    type="email"
                    error={!!errors.email}
                    helperText={errors?.email?.message}
                    variant="outlined"
                    required
                    fullWidth
                  />
                )}
              />

              <Controller
                name="password"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    className="mb-3"
                    placeholder="Password"
                    type={showPassword ? "text" : "password"}
                    error={!!errors.password}
                    helperText={errors?.password?.message}
                    variant="outlined"
                    required
                    fullWidth
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label={
                              showPassword ? "Hide password" : "Show password"
                            }
                            onClick={handleClickShowPassword}
                            onMouseDown={handleMouseDownPassword}
                            edge="end"
                          >
                            {showPassword ? (
                              <VisibilityOff sx={{ fontSize: 19 }} />
                            ) : (
                              <Visibility sx={{ fontSize: 19 }} />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />

              <div className="flex flex-col items-center justify-center sm:flex-row sm:justify-between">
                <Controller
                  name="remember_me"
                  control={control}
                  render={({ field: { value, onChange, ...restField } }) => (
                    <FormControl>
                      <FormControlLabel
                        label="Remember me"
                        control={
                          <Checkbox
                            {...restField}
                            size="small"
                            checked={value}
                            onChange={(e) => onChange(e.target.checked)}
                          />
                        }
                      />
                    </FormControl>
                  )}
                />

                <Link className="text-md font-medium" to="/forgot-password">
                  Forgot password?
                </Link>
              </div>

              <Button
                variant="contained"
                color="secondary"
                className=" mt-7 w-full"
                aria-label="Sign in"
                disabled={isLoading || _.isEmpty(dirtyFields) || !isValid}
                type="submit"
                size="large"
                startIcon={isLoading && <CircularProgress size={20} />}
              >
                Sign in
              </Button>
            </form>
          </div>
        </div>
      </Paper>
    </div>
  );
}

export default AuthJsCredentialsSignInForm;
